import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import compose from 'lodash.flowright';

import CreateWizardLayout from '../../../../../components/CreateWizardLayout';
import WeddingEventReviewStepUpdateModal, {
  WEDDING_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
} from './components/WeddingEventCreateReviewStepUpdate';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import Button from '../../../../../../../components/global/Button';
import EditIcon from '../../../../../../../components/icons/EditIcon';
import { ALBUM_UPLOAD_PERMISSIONS_LABEL } from '../../../../../../../constants/albums';
import { getAsset } from '../../../../../../../util/assets';
import AlbumFolderBranding from '../../../AlbumFolderSettings/AlbumFolderBranding';
import { BASENAME } from '../../../../../../../constants/basenames';
import useBasename from '../../../../../../../components/routing/useBasename';
import DiamondUpgradeButton from '../DiamondUpgradeButton';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    email_address: PropTypes.string,
  }),
  values: PropTypes.shape({
    name: PropTypes.string,
    date: PropTypes.any,
    albums: PropTypes.arrayOf(PropTypes.string),
    join_code: PropTypes.string,
    join_code_confirm: PropTypes.string,
    hash_code: PropTypes.string,
    privacy: PropTypes.string,
  }).isRequired,
  errors: PropTypes.object,
  steps: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  stepProps: PropTypes.object,
  setStepProps: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func,
};

const WeddingEventCreateReviewStep = ({
  user,
  values,
  steps,
  errors,
  handleChange,
  handleBlur,
  stepProps,
  setStepProps,
  onUpgrade,
}) => {
  const openModal = useOpenModal();
  const basename = useBasename();
  const newAlbums = values.albums.filter(album => album.selected);
  const isBasename = basename === BASENAME.SHUTTERBUG;
  const showProfileEmail = isBasename && !user?.email_address;
  const showBrideGroomEmail = !isBasename;
  const showEmail = showProfileEmail || showBrideGroomEmail;

  const { purchasedWeddingEventFeatures, purchasedWeddingEvent } = stepProps;

  return (
    <CreateWizardLayout.Wrapper>
      <CreateWizardLayout.Content size="lg">
        <div className="table-responsive ">
          <table className="table table-striped text-xs text-white-space-normal">
            <tbody>
              <tr>
                <td className="text-left">
                  <div className="d-flex justify-content-center align-items-center">
                    <img
                      src={getAsset('/group-photo-illustration.svg')}
                      alt="group"
                      height="60px"
                      className="mr-2 d-none d-sm-block"
                    />
                    <div className="d-flex align-items-flex-start flex-column mr-1">
                      <h2 className="mb-0">{values.name}</h2>
                      <p className="text-base">
                        {moment(values.date).format('MMMM Do')}
                      </p>
                    </div>
                    <Button
                      btnStyle="link"
                      btnSize="sm"
                      onClick={event => {
                        event.preventDefault();
                        openModal(
                          WEDDING_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                          {
                            step: steps.DETAILS,
                            modalTitle: `Updating ${steps.DETAILS.label}`,
                          },
                        );
                      }}
                      className="review-step-edit-icon ml-auto"
                    >
                      <EditIcon color="#0d84fe" size="22px" className="ml-1" />
                    </Button>
                  </div>
                </td>
              </tr>
              <tr>
                <td className="text-left">
                  <div className="d-flex align-items-center">
                    <div className="d-flex flex-column mr-1">
                      <h3 className="mb-0">Albums</h3>
                      <p className="text-base">
                        {newAlbums.map(album => album.name).join(', ') || 'N/A'}
                      </p>
                    </div>
                    <Button
                      btnStyle="link"
                      className="ml-auto review-step-edit-icon"
                      btnSize="sm"
                      onClick={event => {
                        event.preventDefault();
                        openModal(
                          WEDDING_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                          {
                            step: steps.EVENTS,
                            modalTitle: `Updating ${steps.EVENTS.label}`,
                          },
                        );
                      }}
                    >
                      <EditIcon color="#0d84fe" size="16px" className="ml-1" />
                    </Button>
                  </div>
                  {!purchasedWeddingEventFeatures?.video_guestbook_enabled
                    ?.enabled &&
                    // If the purchased event exists, we're locked down - no upgrades from this wizard.
                    !purchasedWeddingEvent &&
                    onUpgrade && (
                      <div className="wedding-event-create-review-upsell">
                        <div className="mr-1">
                          <p className="text-extra-bold mb-0">Add On</p>
                          <p className="mb-0">
                            Video Guestbook and slideshows for your Rehearsal
                            Dinner &amp; Reception
                          </p>
                        </div>
                        <DiamondUpgradeButton onUpgrade={onUpgrade} />
                      </div>
                    )}
                </td>
              </tr>
              <tr>
                <td className="text-left">
                  <div className="d-flex align-items-center">
                    <div className="d-flex flex-column mr-1">
                      <h3 className="mb-0">Contact info</h3>
                      <p className="text-base">
                        {values.bride_name || 'Luis'} &amp;{' '}
                        {values.groom_name || 'Liliana'}{' '}
                        {showEmail && !showProfileEmail && (
                          <> &ndash; {values.couple_email_address}</>
                        )}
                      </p>
                    </div>
                    <Button
                      btnStyle="link"
                      btnSize="sm"
                      onClick={event => {
                        event.preventDefault();
                        openModal(
                          WEDDING_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                          {
                            step: steps.DETAILS,
                            modalTitle: `Updating ${steps.DETAILS.label}`,
                          },
                        );
                      }}
                      className="ml-auto review-step-edit-icon"
                    >
                      <EditIcon color="#0d84fe" size="22px" className="ml-1" />
                    </Button>
                  </div>
                </td>
              </tr>
              {showEmail && showProfileEmail && (
                <tr>
                  <td className="text-left">
                    <div className="d-flex align-items-center">
                      <div className="d-flex flex-column mr-1">
                        <h3 className="mb-0">Email</h3>
                        <p className="text-base">{values.email_address}</p>
                      </div>
                      <Button
                        btnStyle="link"
                        btnSize="sm"
                        onClick={event => {
                          event.preventDefault();
                          openModal(
                            WEDDING_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                            {
                              step: steps.DETAILS,
                              modalTitle: `Updating ${steps.DETAILS.label}`,
                            },
                          );
                        }}
                        className="ml-auto review-step-edit-icon"
                      >
                        <EditIcon
                          color="#0d84fe"
                          size="22px"
                          className="ml-1"
                        />
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
              <tr>
                <td className="text-left">
                  <div className="d-flex align-items-center">
                    <div className="d-flex flex-column mr-1">
                      <h3 className="mb-0">Upload Permissions</h3>
                      <p className="text-base">
                        {ALBUM_UPLOAD_PERMISSIONS_LABEL[values.permission]}
                      </p>
                    </div>
                    <Button
                      btnStyle="link"
                      className="ml-auto review-step-edit-icon"
                      btnSize="sm"
                      onClick={event => {
                        event.preventDefault();
                        openModal(
                          WEDDING_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                          {
                            step: steps.PERMISSIONS,
                            modalTitle: `Updating ${steps.PERMISSIONS.label}`,
                          },
                        );
                      }}
                    >
                      <EditIcon color="#0d84fe" size="16px" className="ml-1" />
                    </Button>
                  </div>
                </td>
              </tr>
              {(!purchasedWeddingEvent ||
                purchasedWeddingEventFeatures?.join_code_enabled?.enabled) && (
                <tr>
                  <td className="text-left">
                    <div className="d-flex align-items-center">
                      <div className="d-flex flex-column mr-1">
                        <h3 className="mb-0">Join code</h3>
                        <p className="text-base">
                          {purchasedWeddingEventFeatures?.join_code_enabled
                            ?.enabled
                            ? values.join_code || 'N/A'
                            : 'Upgrade to Gold or Platinum to create a personalized SMS code for guests to join your event!'}
                        </p>
                      </div>
                      {purchasedWeddingEventFeatures?.join_code_enabled
                        ?.enabled ? (
                        <Button
                          btnStyle="link"
                          className="ml-auto review-step-edit-icon"
                          btnSize="sm"
                          onClick={event => {
                            event.preventDefault();
                            openModal(
                              WEDDING_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                              {
                                step: steps.JOIN_CODE,
                                modalTitle: `Updating ${steps.JOIN_CODE.label}`,
                              },
                            );
                          }}
                        >
                          <EditIcon
                            color="#0d84fe"
                            size="16px"
                            className="ml-1"
                          />
                        </Button>
                      ) : (
                        <DiamondUpgradeButton onUpgrade={onUpgrade} />
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {(!purchasedWeddingEvent ||
                (purchasedWeddingEventFeatures?.custom_hashtag_enabled
                  ?.enabled &&
                  purchasedWeddingEventFeatures?.photo_watermark_enabled
                    ?.enabled)) && (
                <>
                  <tr>
                    <td className="text-left">
                      <div className="d-flex align-items-center">
                        <div className="d-flex flex-column mr-1">
                          <h3 className="mb-0">Hashtag</h3>
                          <p className="text-base">
                            {purchasedWeddingEventFeatures
                              ?.custom_hashtag_enabled?.enabled ? (
                              <>
                                {values.hash_code
                                  ? `#${values.hash_code}`
                                  : 'N/A'}
                              </>
                            ) : (
                              'Upgrade to Gold or Platinum to add a hashtag!'
                            )}
                          </p>
                        </div>
                        {purchasedWeddingEventFeatures?.custom_hashtag_enabled
                          ?.enabled ? (
                          <Button
                            btnStyle="link"
                            className="ml-auto review-step-edit-icon"
                            btnSize="sm"
                            onClick={event => {
                              event.preventDefault();
                              openModal(
                                WEDDING_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                                {
                                  step: steps.SOCIAL,
                                  modalTitle: `Updating ${steps.SOCIAL.label}`,
                                },
                              );
                            }}
                          >
                            <EditIcon
                              color="#0d84fe"
                              size="16px"
                              className="ml-1"
                            />
                          </Button>
                        ) : (
                          <DiamondUpgradeButton onUpgrade={onUpgrade} />
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="text-left">
                      <div className="d-flex align-items-center">
                        <div className="d-flex flex-column mr-1">
                          <h3 className="mb-0">Photo Overlay</h3>
                          <p className="text-base">
                            {purchasedWeddingEventFeatures
                              ?.photo_watermark_enabled?.enabled ? (
                              <>
                                {values.hash_code
                                  ? `#${values.hash_code}`
                                  : 'N/A'}
                              </>
                            ) : (
                              'Upgrade to Gold or Platinum to add a custom photo overlay to your photos!'
                            )}
                          </p>
                        </div>
                        {purchasedWeddingEventFeatures?.photo_watermark_enabled
                          ?.enabled ? (
                          <Button
                            btnStyle="link"
                            className="ml-auto review-step-edit-icon"
                            btnSize="sm"
                            onClick={event => {
                              event.preventDefault();
                              openModal(
                                WEDDING_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                                {
                                  step: steps.SOCIAL,
                                  modalTitle: `Updating ${steps.SOCIAL.label}`,
                                },
                              );
                            }}
                          >
                            <EditIcon
                              color="#0d84fe"
                              size="16px"
                              className="ml-1"
                            />
                          </Button>
                        ) : (
                          <DiamondUpgradeButton onUpgrade={onUpgrade} />
                        )}
                      </div>

                      {values.watermark_config.asset_image?.id && (
                        <AlbumFolderBranding values={values} disabled />
                      )}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        <WeddingEventReviewStepUpdateModal
          errors={errors}
          handleBlur={handleBlur}
          steps={steps}
          values={values}
          handleChange={handleChange}
          stepProps={stepProps}
          setStepProps={setStepProps}
          user={user}
        />
      </CreateWizardLayout.Content>
    </CreateWizardLayout.Wrapper>
  );
};

WeddingEventCreateReviewStep.propTypes = propTypes;

export default compose()(WeddingEventCreateReviewStep);
